import React, { useMemo, useContext, useEffect, useState } from 'react';
import { useGetItemCategoriesQuery, useGetHospitalVendorsQuery, useSearchItemsQuery } from '../../api/inventorySlice';
import { Stack, InputAdornment, Paper, TextField, Autocomplete, Button, Typography } from '@mui/material';
import { Search, FilterList, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { UserContext } from '../Hooks/useUser';
import GenericTable from '../Tables/GenericTable';
import useTableSearch from '../Hooks/useTableSearch';
import { theme } from '../../theme';

function Inventory() {
    const navigate = useNavigate();
    const { user, hasInventoryManagement, isSuperUser } = useContext(UserContext);
    const [selectedManufacturer, setSelectedManufacturer] = useState('');

    const {
        searchTerm,
        filter: selectedCategory,
        handleSearchTermChange,
        handleFilterChange,
        page,
        setPage,
        resetSearch,
    } = useTableSearch('items');

    const {
        data: fetchedInventory = {},
    } = useSearchItemsQuery({
        hospitalId: user.hospital.id,
        searchParams: {
            search: searchTerm ?? '',
            filters: {
                ...(selectedCategory ? { category: [selectedCategory] } : {}),
                ...(selectedManufacturer ? { manufacturer: [selectedManufacturer] } : {}),
            },
            per_page: 25,
            page,
        },
    });

    const {
        data: itemCategories = []
    } = useGetItemCategoriesQuery(user.hospital.id);

    const {
        data: vendors = []
    } = useGetHospitalVendorsQuery(user.hospital.id);

    const sortedItemCategoryNames = useMemo(() => {
        return itemCategories
            .map(category => category.name)
            .sort((a, b) => a.localeCompare(b));
    }, [itemCategories]);

    const sortedManufacturerNames = useMemo(() => {
        const names = vendors
            .map(vendor => vendor?.name)
            .filter(Boolean);

        return [...new Set(names)].sort((a, b) => a.localeCompare(b));
    }, [vendors]);


    const tablePage = useMemo(() => {
        return page - 1;
    }, [page]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        sessionStorage.setItem('itemsPage', newPage);
    };

    const handleManufacturerChange = (event, newValue) => {
        setSelectedManufacturer(newValue || '');
        setPage(1);
        sessionStorage.setItem('itemsPage', 1);
    };

    const totalEntries = useMemo(() => {
        if (fetchedInventory.meta) {
            return fetchedInventory.meta.total;
        }

        return 0;
    }, [fetchedInventory]);

    useEffect(() => {
        resetSearch();
        setSelectedManufacturer('');
    }, [user.hospital.id]);

    const tableHeaders = [
        'Item Name',
        'Manufacturer',
        'Reference Number',
        'External ID',
        'Category',
        'Price (Each)',
        'Status',
        'Item Trends'
    ];

    return (
        <Stack spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
            <Stack px={{ xs: 2, md: 4 }} pb={2} direction='row' justifyContent='space-between' alignItems='center' sx={{ position: 'sticky', top: 0, zIndex: 5, backgroundColor: theme.palette.background.white, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '20px', letterSpacing: '0.15px' }}>
                    Inventory
                </Typography>
                {hasInventoryManagement() &&
                    <Button variant='contained' size='small' sx={{ display: { xs: 'inline-flex', md: 'none' } }} onClick={() => navigate('/inventory/create')} startIcon={<Add />}>
                        Add New Item
                    </Button>
                }
                <Stack direction='row' justifyContent={{ xs: 'space-between', md: 'start' }} spacing={2} width={{ xs: '100%', md: '70%', lg: '60%' }}>
                    <Paper sx={{ width: '33.33%' }}>
                        <TextField
                            placeholder="Search Inventory"
                            value={searchTerm}
                            onChangeCapture={handleSearchTermChange}
                            size='small'
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <Search />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Paper>
                    <Paper sx={{ width: '33.33%' }}>
                        <Autocomplete
                            options={sortedItemCategoryNames}
                            getOptionLabel={(option) => option}
                            value={selectedCategory}
                            onChange={handleFilterChange}
                            size='small'
                            blurOnSelect
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder='Filter by category'
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <FilterList />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Paper>
                    <Paper sx={{ width: '33.33%' }}>
                        <Autocomplete
                            options={sortedManufacturerNames}
                            getOptionLabel={(option) => option}
                            value={selectedManufacturer}
                            onChange={handleManufacturerChange}
                            size='small'
                            blurOnSelect
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder='Filter by manufacturer'
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <FilterList />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            )}
                        />
                    </Paper>
                </Stack>
                {isSuperUser() &&
                    <Paper sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                        <Button variant='contained' size='small' onClick={() => navigate('/inventory/create')} startIcon={<Add />}>
                            Add New Item
                        </Button>
                    </Paper>
                }
            </Stack>
            <Stack px={2} pb={2} sx={{ flex: 1, minHeight: 0 , overflow: 'hidden' }}>
                <GenericTable
                    data={fetchedInventory.data}
                    dataType='item'
                    headers={tableHeaders}
                    page={tablePage}
                    setPage={handlePageChange}
                    total={totalEntries}
                    rowsPerPage={25}

                />
            </Stack>
        </Stack>
    )
}

export default Inventory;
